import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { QMenuButton, QMenuItem, useFeatureFlag } from "@qualio/ui-components";
import {
  BaseDesignElement,
  DesignElementVersion,
  FeatureFlags,
} from "@design-controls/types";
import { UseQueryResult } from "react-query";
import { toDesignElementVersionLabel } from "../../domain/designElementVersion";

type Props = {
  designElement: Pick<BaseDesignElement, "version" | "major" | "minor">;
  versions: Pick<
    UseQueryResult<DesignElementVersion[]>,
    "data" | "isLoading" | "isError"
  >;
};

export const DesignElementVersionSelect: React.FC<Props> = ({
  designElement,
  versions,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { selectedItemId, version: versionParam } = useParams<{
    selectedItemId: string;
    version?: string;
  }>();
  const versioningEnabled = Boolean(
    useFeatureFlag()[FeatureFlags.MAJOR_MINOR_VERSION_UI],
  );

  const items = versions.data ?? [];
  // The element's own URL (no /version/:version) always shows the latest.
  const isLatest =
    versionParam === undefined ||
    items.some(
      (item) => item.isLatest && item.version === designElement.version,
    );

  const label = (
    version: Parameters<typeof toDesignElementVersionLabel>[0],
    latest: boolean,
  ) =>
    t(
      latest
        ? "designElementVersions.latestLabel"
        : "designElementVersions.label",
      { version: toDesignElementVersionLabel(version).slice(1) },
    );

  const onSelect = useCallback(
    (item: DesignElementVersion) => () => {
      // Everything up to and including the element id, so the version is
      // swapped whether the page is on the element or one of its versions.
      const elementPath = pathname.slice(
        0,
        pathname.indexOf(`/${selectedItemId}`) + `/${selectedItemId}`.length,
      );
      navigate(
        item.isLatest
          ? elementPath
          : `${elementPath}/version/${toDesignElementVersionLabel(item)}`,
      );
    },
    [navigate, pathname, selectedItemId],
  );

  if (!versioningEnabled) {
    return null;
  }

  return (
    <QMenuButton
      buttonLabel={label(designElement, isLatest)}
      variant="ghost"
      isDisabled={versions.isLoading || versions.isError || items.length <= 1}
      data-cy="design-element-version-select"
    >
      {items.map((item) => {
        const isCurrent = item.version === designElement.version;
        return (
          <QMenuItem
            key={item.version}
            onClick={onSelect(item)}
            bg={isCurrent ? "gray.100" : undefined}
            aria-current={isCurrent ? "true" : undefined}
            data-cy={`design-element-version-${toDesignElementVersionLabel(item)}`}
          >
            {label(item, item.isLatest)}
          </QMenuItem>
        );
      })}
    </QMenuButton>
  );
};
